import { useConfigStore } from "@/store/config";
import { useCryptStore } from "@/store/crypt";
import { useTokenStore } from "@/store/token";
import axios from "axios";
import { Buffer } from "buffer";
import { secureStorage } from "./aes";
import i18n from "./i18n";
import { globalMessage } from "./notificationManager";

export interface LoginParams {
  account: string;
  password: string;
  captcha: string;
}

export interface AccessToken {
  access_token: string;
  refresh_token: string;
}

export interface ApiResponse<T> {
  code: number;
  message: string;
  data?: T;
}

export interface UploadPixelArtParams {
  imageDataUri?: string;
  filename?: string;
  paletteName: string;
  imageModelProvider?: "ark" | "qwen";
  width: number;
  height: number;
  colorLimit: number;
  generationMode?: "text" | "direct" | "ai_image" | "ai_image_text";
  editPrompt?: string;
}

export interface UploadPatternLegendItem {
  code: string;
  hex: string;
  count: number;
}

export interface UploadPatternCell {
  x: number;
  y: number;
  code: string;
  hex: string;
}

export interface UploadPatternMeta {
  width: number;
  height: number;
  backgroundCode: string;
  totalBeads: number;
}

export interface UploadPixelArtResult {
  pixelArtId: string;
  imageUrl: string;
  patternImageUrl?: string;
  patternGrid?: UploadPatternCell[][];
  patternMeta?: UploadPatternMeta;
  paletteName: string;
  width: number;
  height: number;
  totalBeads: number;
  legend: UploadPatternLegendItem[];
}

export const noauthApi = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});
type tokenType = "access_token" | "refresh_token";
let isRefreshing = false;
const failedQueue: {
  resolve: (value: string) => void;
  reject: (reason?: any) => void;
}[] = [];
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue.length = 0;
};
const makeEncryptedRequest = (
  url: string,
  tokenType: tokenType = "access_token",
) => {
  const api = axios.create({
    baseURL: url,
    headers: {
      "Content-Type": "application/x-bcs",
    },
    withCredentials: true,
  });

  api.interceptors.request.use(async (config) => {
    const tokenStore = useTokenStore.getState();
    switch (tokenType) {
      case "access_token":
        if (tokenStore.accessToken) {
          config.headers.Authorization = `Bearer ${tokenStore.accessToken}`;
        }
        break;
      case "refresh_token":
        if (tokenStore.refreshToken) {
          config.headers.Authorization = `Bearer ${tokenStore.refreshToken}`;
        }
        break;
    }
    const encryptedPubkey = localStorage.getItem("local_public_key");
    if (encryptedPubkey) {
      const publicKey = await secureStorage.decrypt(encryptedPubkey);
      console.debug("Attaching Public Key to Request:", publicKey);
      config.headers["X-Client-Public-Key"] = publicKey;
      config.headers.Accept = ["application/json", "application/x-bcs"];
      config.responseType =
        config.responseType === "blob" ? "blob" : "arraybuffer";
    }
    config.headers["Accept-Language"] = useConfigStore.getState().locale;
    return config;
  });
  api.interceptors.response.use(
    async (response) => {
      console.debug("Received Response:", response);
      if (response.config.responseType === "blob") {
        if (response.headers["content-type"] === "application/x-bcs") {
          console.debug("Decrypting Response Data with AES-GCM");
          const cryptStoreState = useCryptStore.getState();
          response.data = await cryptStoreState.aes_gcm_decrypt(
            new Uint8Array(response.data).buffer,
          );
        }
        return response;
      }
      if (response.config.responseType === "arraybuffer" && response.data) {
        if (response.headers["content-type"] === "application/x-bcs") {
          console.debug("Decrypting Response Data with AES-GCM");
          const cryptStoreState = useCryptStore.getState();
          response.data = await cryptStoreState.aes_gcm_decrypt(
            new Uint8Array(response.data).buffer,
          );
        }
        const decodedString = Buffer.from(response.data).toString();
        response.data = JSON.parse(decodedString);
        console.debug("Decrypted Response Data:", response.data);
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      const requestUrl = String(originalRequest?.url || "");
      const isRefreshRequest = requestUrl.includes("/auth/user/refresh");
      const isLogoutRequest = requestUrl.includes("/auth/user/logout");
      if (error.config.responseType === "arraybuffer") {
        if (
          error.response?.data &&
          error.response?.headers["content-type"] === "application/x-bcs"
        ) {
          const cryptStoreState = useCryptStore.getState();
          error.response.data = await cryptStoreState.aes_gcm_decrypt(
            new Uint8Array(error.response.data).buffer,
          );
        }
      }
      const decodedString = Buffer.from(error.response.data).toString();
      error.response.data = JSON.parse(decodedString);
      const status = error.response ? error.response.status : null;
      const errorMessage = error.response?.data?.message ?? null;
      const { t } = i18n;
      if (status === 401 && isRefreshRequest) {
        if (!isLogoutRequest) {
          await useTokenStore.getState().logout();
        }
        return Promise.reject(error);
      }

      if (status === 401 && !originalRequest._retry) {
        const tokenState = useTokenStore.getState();
        if (isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              return api(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        return new Promise((resolve, reject) => {
          tokenState
            .refresh()
            .then((newToken) => {
              processQueue(null, newToken);
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              resolve(api(originalRequest));
            })
            .catch(async (refreshError) => {
              processQueue(refreshError, null);
              await useTokenStore.getState().logout();
              reject(refreshError);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }
      switch (status) {
        case 400:
          return Promise.reject(errorMessage);
        case 403:
          globalMessage.error(errorMessage || t("auth.forbidden"));
          break;
        case 500:
          globalMessage.error(errorMessage || t("auth.serverError"));
          break;
        case 503:
          return Promise.reject(errorMessage || t("modelProvider.unavailable"));
        default:
          globalMessage.error(errorMessage || t("auth.unknownError"));
      }
    },
  );
  return api;
};
export const authApi = makeEncryptedRequest(
  import.meta.env.VITE_API_BASE_URL || "/api",
);
export const refreshApi = makeEncryptedRequest(
  import.meta.env.VITE_API_BASE_URL || "/api",
  "refresh_token",
);
export const graphqlApi = makeEncryptedRequest(
  import.meta.env.VITE_GRAPHQL_API_URL || "/graphql",
);

export async function createPixelArtFromUpload(params: UploadPixelArtParams) {
  const cryptStore = useCryptStore.getState();
  const encryptedData = await cryptStore.aes_gcm_encrypt(
    Buffer.from(JSON.stringify(params)).buffer,
  );
  const response = await authApi.post<ApiResponse<UploadPixelArtResult>>(
    "/pixel-art/from-upload",
    encryptedData,
    { timeout: 300_000 },
  );
  if (response.data.code !== 0 || !response.data.data) {
    throw new Error(response.data.message);
  }
  return response.data.data;
}
